import { useEffect, useState } from 'react';
import { Plus, Table, History, Pin, PinOff } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { Item } from '../../services/types';
import { ReceiveStockModal } from './ReceiveStockModal';
import { StockAdjustmentModal } from './StockAdjustmentModal';
import { StockLedger } from './StockLedger';
import { FgMovementModal } from './FgMovementModal';
import { cn } from '../../lib/utils';
import { CompactTable } from '../../components/ui/CompactTable';
import { Button } from '../../components/ui/button';
import { Tabs } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { useSettings } from '../../context/SettingsContext';
import { useNavigationProfile } from '../../hooks/useNavigationProfile';
import { buildInventoryPinnedView, type InventoryFilterType, type InventoryTab } from './pinnedView';
import { isEnabledFlag } from '../../lib/featureFlags';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { includesNormalized } from '../../components/table-filters/filterPredicates';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';

const VALID_TABS = new Set<InventoryTab>(['status', 'ledger']);
const VALID_TYPES = new Set<InventoryFilterType>(['all', 'stock', 'raw', 'fg', 'service', 'consumable', 'fixed_asset']);

const normalizeItemType = (value?: string | null): 'Stock' | 'Consumable' | 'Service' | 'FixedAsset' => {
    const key = String(value || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
    if (['consumable', 'consumables', 'nonstock', 'expense'].includes(key)) return 'Consumable';
    if (['service', 'services', 'labour', 'labor'].includes(key)) return 'Service';
    if (['fixedasset', 'asset', 'fa'].includes(key)) return 'FixedAsset';
    return 'Stock';
};

export function InventoryList() {
    const [items, setItems] = useState<Item[]>([]);
    const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
    const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
    const [isFgMovementModalOpen, setIsFgMovementModalOpen] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { state: filterState, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        so_status: 'all',
        wo_status: 'all'
    });
    const { getSetting } = useSettings();
    const navigationV2Enabled = isEnabledFlag(getSetting('ui_navigation_v2_enabled'));
    const { pinnedViews, togglePinnedView } = useNavigationProfile(navigationV2Enabled);

    const parsedTab = searchParams.get('tab');
    const parsedType = searchParams.get('type');
    const activeTab: InventoryTab = VALID_TABS.has(parsedTab as InventoryTab) ? (parsedTab as InventoryTab) : 'status';
    const filterType: InventoryFilterType = VALID_TYPES.has(parsedType as InventoryFilterType)
        ? (parsedType as InventoryFilterType)
        : 'all';

    const currentPinnedView = buildInventoryPinnedView(activeTab, filterType);
    const isCurrentViewPinned = (pinnedViews || []).some((view) => view.id === currentPinnedView.id);

    const tabs = [
        { id: 'status', label: 'Current Status', icon: Table },
        { id: 'ledger', label: 'Stock Ledger', icon: History }
    ];

    useEffect(() => {
        loadItems();
    }, []);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        let changed = false;
        if (next.get('tab') !== activeTab) {
            next.set('tab', activeTab);
            changed = true;
        }
        if (activeTab === 'status') {
            if (next.get('type') !== filterType) {
                next.set('type', filterType);
                changed = true;
            }
        } else if (next.has('type')) {
            next.delete('type');
            changed = true;
        }
        if (changed) {
            setSearchParams(next, { replace: true });
        }
    }, [activeTab, filterType, searchParams, setSearchParams]);

    const loadItems = () => {
        api.inventory.list().then(setItems);
    };

    const updateViewState = (nextTab: InventoryTab, nextType?: InventoryFilterType) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', nextTab);
        if (nextTab === 'status') {
            params.set('type', nextType || filterType);
        } else {
            params.delete('type');
        }
        setSearchParams(params, { replace: true });
    };

    const isServiceItem = (item: Item): boolean =>
        (item.item_role || '').toUpperCase() === 'SERVICE' || normalizeItemType(item.item_type) === 'Service';
    const isConsumableItem = (item: Item): boolean =>
        ['CONSUMABLE', 'NON_STOCK', 'NONSTOCK'].includes((item.item_role || '').toUpperCase()) || normalizeItemType(item.item_type) === 'Consumable';
    const isFixedAssetItem = (item: Item): boolean =>
        ['ASSET', 'FIXED_ASSET'].includes((item.item_role || '').toUpperCase()) || normalizeItemType(item.item_type) === 'FixedAsset';
    const isFinishedGoodItem = (item: Item): boolean => (item.item_role || '').toUpperCase() === 'FG';
    const isStockItem = (item: Item): boolean => normalizeItemType(item.item_type) === 'Stock' && !isServiceItem(item) && !isConsumableItem(item) && !isFixedAssetItem(item);
    const isRawItem = (item: Item): boolean => isStockItem(item) && !isFinishedGoodItem(item);

    const soStatusOptions = Array.from(
        new Set(
            items
                .filter((item) => isFinishedGoodItem(item))
                .flatMap((item) => item.so_statuses || [])
                .filter(Boolean)
        )
    ).sort((a, b) => a.localeCompare(b));

    const woStatusOptions = Array.from(
        new Set(
            items
                .filter((item) => isFinishedGoodItem(item))
                .flatMap((item) => item.wo_statuses || [])
                .filter(Boolean)
        )
    ).sort((a, b) => a.localeCompare(b));

    const filteredItems = items.filter(item => {
        const matchesSearch = includesNormalized(
            [
                item.code,
                item.name,
                item.category,
                item.uom,
                item.item_role,
                item.item_type
            ].join(' '),
            filterState.q
        );
        if (!matchesSearch) return false;
        if (filterType === 'all') return true;
        if (filterType === 'stock') return isStockItem(item);
        if (filterType === 'raw') return isRawItem(item);
        if (filterType === 'fg') {
            if (!isFinishedGoodItem(item)) return false;
            if (filterState.so_status !== 'all' && !(item.so_statuses || []).includes(filterState.so_status)) return false;
            if (filterState.wo_status !== 'all' && !(item.wo_statuses || []).includes(filterState.wo_status)) return false;
            return true;
        }
        if (filterType === 'service') return isServiceItem(item);
        if (filterType === 'consumable') return isConsumableItem(item);
        return isFixedAssetItem(item);
    });

    const totalCount = items.length;
    const stockCount = items.filter(isStockItem).length;
    const rawCount = items.filter(isRawItem).length;
    const fgCount = items.filter(isFinishedGoodItem).length;
    const serviceCount = items.filter(isServiceItem).length;
    const consumableCount = items.filter(isConsumableItem).length;
    const fixedAssetCount = items.filter(isFixedAssetItem).length;

    const columns = [
        {
            header: 'Code', accessor: (i: Item) => (
                <div className="flex flex-col items-center gap-1">
                    <span className="font-mono font-semibold">{i.code}</span>
                    {isFinishedGoodItem(i) && (
                        <Badge variant="secondary" className="px-1 py-0 text-[10px] h-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200">
                            FG
                        </Badge>
                    )}
                    {isRawItem(i) && (
                        <Badge variant="secondary" className="px-1 py-0 text-[10px] h-4 bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-200">
                            Raw
                        </Badge>
                    )}
                    {isServiceItem(i) && (
                        <Badge variant="secondary" className="px-1 py-0 text-[10px] h-4 bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200">
                            Service
                        </Badge>
                    )}
                    {isConsumableItem(i) && (
                        <Badge variant="secondary" className="px-1 py-0 text-[10px] h-4 bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200">
                            Consumable
                        </Badge>
                    )}
                    {isFixedAssetItem(i) && (
                        <Badge variant="secondary" className="px-1 py-0 text-[10px] h-4 bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200">
                            Asset
                        </Badge>
                    )}
                </div>
            ), className: 'w-[12%] text-center'
        },
        { header: 'Item Name', accessor: (i: Item) => <span className="whitespace-normal break-words">{i.name}</span>, className: 'w-[20%] text-left px-2' },
        { header: 'Category', accessor: (i: Item) => i.category ?? '', className: 'w-[12%] text-center text-sm text-slate-600' },
        { header: 'UOM', accessor: (i: Item) => i.uom, className: 'w-[8%] text-center text-sm text-muted-foreground' },
        {
            header: 'Stock', accessor: (i: Item) => {
                if (!isStockItem(i)) return <span className="text-muted-foreground">-</span>;
                const cls = i.current_stock <= 0 ? 'text-destructive' : i.current_stock < 5 ? 'text-amber-600' : 'text-foreground';
                return (
                    <div className="flex justify-center">
                        <span className={cn('font-medium', cls)}>{Number(i.current_stock).toFixed(2)}</span>
                    </div>
                );
            }, className: 'w-[10%] text-center'
        },
        {
            header: 'WAC Cost', accessor: (i: Item) => {
                if (!isStockItem(i)) return <span className="text-muted-foreground">-</span>;
                return <div className="flex justify-center"><span className="text-sm text-slate-700">{Number(i.wac_cost).toFixed(2)}</span></div>;
            }, className: 'w-[12%] text-center'
        },
        {
            header: 'Value',
            accessor: (i: Item) => {
                if (!isStockItem(i)) return <span className="text-muted-foreground">-</span>;
                const val = Number(i.current_stock) * Number(i.wac_cost);
                return (
                    <div className="flex justify-center">
                        <span className={cn('font-semibold', val < 0 ? 'text-destructive' : 'text-foreground')}>
                            {val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    </div>
                );
            },
            className: 'w-[12%] text-center'
        },
        {
            header: 'Actions',
            accessor: (i: Item) => {
                const disabled = !isStockItem(i);
                const isFg = isFinishedGoodItem(i);
                const soContext = (i.so_statuses || []).slice(0, 2).join(', ');
                const woContext = (i.wo_statuses || []).slice(0, 2).join(', ');
                return (
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex justify-center gap-1">
                            {isFg ? (
                                <Button
                                    size="sm"
                                    className="h-7 px-2 text-[11px]"
                                    disabled={disabled}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (disabled) return;
                                        setSelectedItemId(i.id);
                                        setIsFgMovementModalOpen(true);
                                    }}
                                >
                                    Post
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-[11px]"
                                        disabled={disabled}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (disabled) return;
                                            setSelectedItemId(i.id);
                                            setIsAdjustmentModalOpen(true);
                                        }}
                                    >
                                        Adjust
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="h-7 px-2 text-[11px]"
                                        disabled={disabled}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (disabled) return;
                                            setSelectedItemId(i.id);
                                            setIsReceiptModalOpen(true);
                                        }}
                                    >
                                        Update
                                    </Button>
                                </>
                            )}
                        </div>
                        {(soContext || woContext) && (
                            <span className="text-[10px] text-slate-500 text-center leading-tight">
                                {soContext ? `SO: ${soContext}` : ''}
                                {soContext && woContext ? ' | ' : ''}
                                {woContext ? `WO: ${woContext}` : ''}
                            </span>
                        )}
                    </div>
                );
            },
            className: 'w-[14%] text-center'
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end pb-2 border-b">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Inventory Management</h1>
                    <p className="text-muted-foreground italic">Stock Control & Goods Receipt</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        PO/GRN and Return flows post stock automatically. Use `Post FG Movement` for finished goods and `Update/Adjustment` for raw-material internal corrections.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            setSelectedItemId(null);
                            setIsAdjustmentModalOpen(true);
                        }}
                    >
                        Adjustment
                    </Button>
                    <Button
                        onClick={() => {
                            setSelectedItemId(null);
                            setIsReceiptModalOpen(true);
                        }}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Update Stock
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setSelectedItemId(null);
                            setIsFgMovementModalOpen(true);
                        }}
                    >
                        Post FG Movement
                    </Button>
                    {navigationV2Enabled && (
                        <Button
                            variant={isCurrentViewPinned ? 'secondary' : 'outline'}
                            onClick={async () => {
                                await togglePinnedView(currentPinnedView);
                            }}
                        >
                            {isCurrentViewPinned ? <PinOff className="mr-2 h-4 w-4" /> : <Pin className="mr-2 h-4 w-4" />}
                            {isCurrentViewPinned ? 'Unpin This View' : 'Pin This View'}
                        </Button>
                    )}
                </div>
            </div>

            <Tabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={(id) => updateViewState(id as InventoryTab)}
                variant="underline"
            />

            {activeTab === 'status' ? (
                <div className="space-y-4">
                    <FlowEntryBanner
                        title="Inventory Flow Guidance"
                        pathLabel="Internal Update vs Valued Receipt"
                        nextHint="Use Update/Adjust for internal qty control. Use PO/GRN for supplier valued receipts."
                        helpModule="inventory"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Internal Stock Update</p>
                            <p className="text-sm text-emerald-800">Qty-only operational flow for SO/WO completion, opening balances, and corrections.</p>
                            <p className="text-xs text-emerald-700 mt-1">Impact: Inventory only, no accounting journal.</p>
                        </div>
                        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Valued Supplier Receipt</p>
                            <p className="text-sm text-blue-800">Use PO/GRN for supplier receipts where costing and accounting are required.</p>
                            <p className="text-xs text-blue-700 mt-1">Impact: Inventory + purchasing/finance postings.</p>
                        </div>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant={filterType === 'all' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'all')}
                        >
                            All Items ({totalCount})
                        </Button>
                        <Button
                            variant={filterType === 'stock' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'stock')}
                        >
                            Stock Only ({stockCount})
                        </Button>
                        <Button
                            variant={filterType === 'raw' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'raw')}
                        >
                            Raw Materials ({rawCount})
                        </Button>
                        <Button
                            variant={filterType === 'fg' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'fg')}
                        >
                            Finished Goods ({fgCount})
                        </Button>
                        <Button
                            variant={filterType === 'service' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'service')}
                        >
                            Services Only ({serviceCount})
                        </Button>
                        <Button
                            variant={filterType === 'consumable' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'consumable')}
                        >
                            Consumables ({consumableCount})
                        </Button>
                        <Button
                            variant={filterType === 'fixed_asset' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => updateViewState('status', 'fixed_asset')}
                        >
                            Fixed Assets ({fixedAssetCount})
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        `Stock Only` includes valued stock items only. Consumables, services, and fixed assets are visible for reference but blocked from stock movement actions.
                    </p>
                    <TableFilterBar
                        searchValue={filterState.q}
                        onSearchChange={(value) => setFilter('q', value)}
                        searchPlaceholder="Search item code, name, category..."
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                    {filterType === 'fg' && (
                        <div className="flex flex-wrap gap-2 items-center">
                            <select
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                                value={filterState.so_status}
                                onChange={(event) => setFilter('so_status', event.target.value)}
                            >
                                <option value="all">All SO Statuses</option>
                                {soStatusOptions.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                            <select
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                                value={filterState.wo_status}
                                onChange={(event) => setFilter('wo_status', event.target.value)}
                            >
                                <option value="all">All WO Statuses</option>
                                {woStatusOptions.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setFilter('so_status', 'all');
                                    setFilter('wo_status', 'all');
                                }}
                            >
                                Clear Status Filters
                            </Button>
                        </div>
                    )}
                    <div className="bg-card border rounded-md shadow-sm">
                        <CompactTable data={filteredItems} columns={columns} keyField="id" />
                    </div>
                </div>
            ) : (
                <StockLedger />
            )}

            {isReceiptModalOpen && (
                <ReceiveStockModal
                    isOpen={isReceiptModalOpen}
                    initialItemId={selectedItemId}
                    onClose={() => {
                        setIsReceiptModalOpen(false);
                        setSelectedItemId(null);
                    }}
                    onSuccess={() => {
                        setIsReceiptModalOpen(false);
                        setSelectedItemId(null);
                        loadItems();
                    }}
                />
            )}

            {isAdjustmentModalOpen && (
                <StockAdjustmentModal
                    isOpen={isAdjustmentModalOpen}
                    initialItemId={selectedItemId}
                    onClose={() => {
                        setIsAdjustmentModalOpen(false);
                        setSelectedItemId(null);
                    }}
                    items={items}
                    onSuccess={() => {
                        setIsAdjustmentModalOpen(false);
                        setSelectedItemId(null);
                        loadItems();
                    }}
                />
            )}

            {isFgMovementModalOpen && (
                <FgMovementModal
                    isOpen={isFgMovementModalOpen}
                    initialItemId={selectedItemId}
                    items={items}
                    onClose={() => {
                        setIsFgMovementModalOpen(false);
                        setSelectedItemId(null);
                    }}
                    onSuccess={() => {
                        setIsFgMovementModalOpen(false);
                        setSelectedItemId(null);
                        loadItems();
                    }}
                />
            )}
        </div>
    );
}
